(ns dashboard.appsync.core
  (:require
   [re-frame.core :as re-frame]
   [dashboard.appsync.internals :as internals
    :refer [interceptors default-instance-name]]
   [dashboard.utils :as utils :refer [->json]]
   [dashboard.config :as config]
   [clojure.string :as string]))

(re-frame/reg-event-fx
 ::mutate
 interceptors
 (fn [{:keys [db dispatchable-event instance-name]} [query-id query variables callback-event :as event]]
   (let [query (str "mutation " (string/replace query #"^mutation\s?" ""))]
     (cond
       (or (get-in db [:http :requests query-id])
           (get-in db [:subscriptions query-id]))
       {} ;; duplicate in-flight mutation

       (get-in db [:ws :ready?])
       {:db (assoc-in db [:subscriptions query-id] {:callback callback-event})
        ::internals/send-ws [(get-in db [:ws :connection])
                             {:id query-id
                              :type "start"
                              :payload {:query query
                                        :variables variables}}]}

       :else
       {:db (assoc-in db [:http :requests query-id] {:callback callback-event})
        ::internals/send-http [instance-name
                               query-id
                               (get-in db [:http :url])
                               {:request (get-in db [:http :impl])
                                :payload {:query query
                                          :variables variables}}]}))))

(defn mutate
  "Execute a GraphQL mutation. The arguments are:
  [instance-name query-string variables callback]
  If the optional `instance-name` is not provided, the default instance is
  used. The callback function will receive the result of the mutation as its
  sole argument."
  [& args]
  (let [callback-fn (last args)]
    (re-frame/dispatch (into [::mutate] (conj (vec (butlast args)) [::internals/callback callback-fn])))))

(re-frame/reg-event-fx
 ::query
 interceptors
 (fn [{:keys [db token dispatchable-event instance-name]} [query-id query variables callback-event :as event]]
   (let [query (str "subscription " (string/replace query #"^query\s?" ""))]
     (cond
       (or (get-in db [:http :requests query-id])
           (get-in db [:subscriptions query-id]))
       {} ;; duplicate in-flight query

       ;; {:db (assoc-in db [:subscriptions query-id] {:callback callback-event})
       ;;  ::internals/send-ws [(get-in db [:ws :connection])
       ;;                       {:id query-id
       ;;                        :type "start"
       ;;                        :payload {:data
       ;;                                  (.stringify js/JSON (clj->js
       ;;                                                       {:query query
       ;;                                                        :variables variables}))
       ;;                                  :extensions
       ;;                                  {:authorization
       ;;                                   {"host" "cra73qox6vc3lbrpdtmaszqtfa.appsync-api.ap-northeast-1.amazonaws.com"
       ;;                                    "x-api-key" "da2-hrqwbjpc75aztpamk6epgtims4"}}}}]}

       :else
       {:db (assoc-in db [:http :requests query-id] {:callback callback-event})
        ::internals/send-http [instance-name
                               query-id
                               (get-in db [:http :url])
                               {:request (get-in db [:http :impl])
                                :payload {:data
                                          (.stringify js/JSON (clj->js
                                                               {:query query
                                                                :variables variables}))
                                          :extensions
                                          {:authorization
                                           {"host" config/graphql-host
                                            "x-api-key" token}}}}]}))))

(defn query
  "Execute a GraphQL query. The arguments are:
  [instance-name query-string variables callback]
  If the optional `instance-name` is not provided, the default instance is
  used. The callback function will receive the result of the query as its
  sole argument."
  [& args]
  (let [callback-fn (last args)]
    (re-frame/dispatch (into [::query] (conj (vec (butlast args)) [::internals/callback callback-fn])))))

(re-frame/reg-event-fx
 ::abort
 interceptors
 (fn [{:keys [db]} [query-id]]
   (merge
    {:db (-> db
             (update :subscriptions dissoc query-id)
             (update-in [:http :requests] dissoc query-id))}
    (when-let [abort-fn (get-in db [:http :requests query-id :abort])]
      {::internals/call-abort abort-fn}))))

(defn abort
  ([query-id] (abort default-instance-name query-id))
  ([instance-name query-id]
   (re-frame/dispatch [::abort instance-name query-id])))

(re-frame/reg-event-fx
 ::subscribe
 interceptors
 (fn [{:keys [db token instance-name dispatchable-event] :as cofx}
      [subscription-id query variables callback-event :as event]]
   (cond
     (get-in db [:subscriptions (name subscription-id) :active?])
     {} ;; duplicate subscription

     (get-in db [:ws :ready?])
     {:db (assoc-in db [:subscriptions (name subscription-id)] {:callback callback-event
                                                                :event dispatchable-event
                                                                :active? true})
      ::internals/send-ws [(get-in db [:ws :connection])
                           {:id (name subscription-id)
                            :type "start"
                            :payload {:data
                                      (->json
                                       {:query query
                                        :variables variables})
                                      :extensions
                                      {:authorization
                                       {"host" config/graphql-host
                                        "Authorization" token}}}}]}

     (:ws db)
     {:db (update-in db [:ws :queue] conj dispatchable-event)}

     :else
     (re-frame/console
      :error
      (str
       "Error creating subscription " subscription-id
       " on instance " instance-name
       ": Websocket is not enabled, subscriptions are not possible. Please check your re-graph configuration")))))

(defn subscribe
  ([subscription-id query variables callback-fn] (subscribe default-instance-name subscription-id query variables callback-fn))
  ([instance-name subscription-id query variables callback-fn]
   (re-frame/dispatch [::subscribe instance-name subscription-id query variables [::internals/callback callback-fn]])))

(re-frame/reg-event-fx
 ::unsubscribe
 interceptors
 (fn [{:keys [db instance-name]} [subscription-id :as event]]
   (if (get-in db [:ws :ready?])
     {:db (update db :subscriptions dissoc (name subscription-id))
      ::internals/send-ws [(get-in db [:ws :connection])
                           {:id (name subscription-id)
                            :type "stop"}]}

     {:db (update-in db [:ws :queue] conj [::unsubscribe instance-name subscription-id])})))

(defn unsubscribe
  ([subscription-id] (unsubscribe default-instance-name subscription-id))
  ([instance-name subscription-id]
   (re-frame/dispatch [::unsubscribe instance-name subscription-id])))

(re-frame/reg-event-fx
 ::init
 (fn [{:keys [db]} [_ instance-name opts]]
   (let [[instance-name opts] (cond
                                (and (nil? instance-name) (nil? opts))
                                [default-instance-name {}]

                                (map? instance-name)
                                [default-instance-name instance-name]

                                (nil? instance-name)
                                [default-instance-name opts]

                                :else
                                [instance-name opts])
         ws-options (internals/ws-options opts)
         http-options (internals/http-options opts)]

     (merge
      {:db (assoc-in db [:re-graph instance-name]
                     (merge ws-options http-options))}
      (when ws-options
        {::internals/connect-ws [instance-name ws-options]})))))

(re-frame/reg-event-fx
 ::destroy
 interceptors
 (fn [{:keys [db instance-name]} _]
   (if-let [subscription-ids (not-empty (-> db :subscriptions keys))]
     {:dispatch-n (for [subscription-id subscription-ids]
                    [::unsubscribe instance-name subscription-id])
      :dispatch [::destroy instance-name]}

     (merge
      {:db (assoc db :destroyed? true)}
      (when-let [ws (get-in db [:ws :connection])]
        {::internals/disconnect-ws [ws]})))))

(defn init
  ([opts] (init default-instance-name opts))
  ([instance-name opts]
   (re-frame/dispatch [::init instance-name opts])))

(defn destroy
  ([] (destroy default-instance-name))
  ([instance-name]
   (re-frame/dispatch [::destroy instance-name])))
